/**
 * VaultStoryStage — rotating 5-chapter narrative for the /login canvas.
 *
 * Chapters (~6s each, ~30s loop):
 *   01 GLOBAL   — currency symbols drift, then anchor around one VAULT.
 *   02 CARDS    — cards go from scattered to a neat organised stack.
 *   03 WEALTH   — asset buckets consolidate into net worth.
 *   04 LIFE     — a day's financial events land on a timeline.
 *   05 TOGETHER — mine + partner + joint, warmly separated.
 *
 * Rules honoured:
 *   • No real balances or account data — pre-auth surface.
 *   • Respects prefers-reduced-motion → static poster.
 *   • Manual dot selects a story and pauses auto-rotation ~15 s.
 *   • Fires analytics events via optional window.vlxAnalytics(fn).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import {
    Globe2,
    CreditCard,
    Wallet,
    Clock,
    Users,
    ShieldCheck,
} from "lucide-react";

const STORIES = [
    { key: "global",   label: "Global" },
    { key: "cards",    label: "Cards" },
    { key: "wealth",   label: "Wealth" },
    { key: "life",     label: "Life" },
    { key: "together", label: "Together" },
];

const STORY_MS = 6000;
const RESUME_AFTER_MANUAL_MS = 15000;

function prefersReducedMotion() {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function track(event, meta) {
    try {
        if (typeof window !== "undefined" && typeof window.vlxAnalytics === "function") {
            window.vlxAnalytics(event, meta || {});
        }
    } catch { /* silent */ }
}

export default function VaultStoryStage() {
    const reduced = useMemo(prefersReducedMotion, []);
    const [idx, setIdx] = useState(0);
    const [paused, setPaused] = useState(reduced);
    const pauseTimer = useRef(null);
    const rotateTimer = useRef(null);

    /* auto-rotation (unless reduced-motion or user just picked a story) */
    useEffect(() => {
        if (paused) return undefined;
        rotateTimer.current = setTimeout(() => {
            setIdx((i) => (i + 1) % STORIES.length);
        }, STORY_MS);
        return () => clearTimeout(rotateTimer.current);
    }, [idx, paused]);

    /* first-view analytics on chapter change */
    useEffect(() => {
        track(`login_story_${STORIES[idx].key}_view`);
    }, [idx]);

    const pick = (i) => {
        setIdx(i);
        setPaused(true);
        track("login_story_manual_select", { chapter: STORIES[i].key });
        clearTimeout(pauseTimer.current);
        pauseTimer.current = setTimeout(() => setPaused(false), RESUME_AFTER_MANUAL_MS);
    };

    return (
        <section className="vlx-stage" data-testid="vlx-stage">
            <div className="vlx-stage__grain" aria-hidden />

            <header className="vlx-stage__head">
                <div className="vlx-stage__brand" data-testid="vlx-brand">
                    <span className="vlx-stage__brand-mark" aria-hidden>
                        <ShieldCheck size={22} strokeWidth={1.7} />
                    </span>
                    <span>VAULT</span>
                    <span className="vlx-stage__brand-tag">by PD</span>
                </div>
                <div className="vlx-stage__chip" aria-hidden="true">
                    <span className="vlx-stage__chip-dot" />
                    Personal Financial OS
                </div>
            </header>

            <div className="vlx-scene" role="region" aria-label="VAULT product story">
                <div className="vlx-scene__inner">
                    {/* ── Story 01 · Global ─────────────────────────── */}
                    <article
                        className={`vlx-story ${idx === 0 ? "is-active" : ""}`}
                        aria-hidden={idx !== 0}
                        data-testid="vlx-story-global"
                    >
                        <div className="vlx-globe" aria-hidden>
                            <span className="vlx-globe__symbol s-1">₹</span>
                            <span className="vlx-globe__symbol s-2">$</span>
                            <span className="vlx-globe__symbol s-3">€</span>
                            <span className="vlx-globe__symbol s-4">£</span>
                            <span className="vlx-globe__symbol s-5">¥</span>
                            <span className="vlx-globe__symbol s-6">₩</span>
                            <span className="vlx-globe__symbol s-7">₣</span>
                            <span className="vlx-globe__symbol s-8">د.إ</span>
                        </div>
                        <div className="vlx-story__eyebrow">
                            <Globe2 size={11} style={{ verticalAlign: "-2px", marginRight: 6 }} /> Global money
                        </div>
                        <h2 className="vlx-story__h">
                            Money looks different around the world.<br />
                            <span className="accent">Organisation shouldn&rsquo;t.</span>
                        </h2>
                        <div className="vlx-currencies">
                            {[
                                ["₹", "INR"], ["$", "USD"], ["€", "EUR"],
                                ["£", "GBP"], ["¥", "JPY"], ["د.إ", "AED"],
                            ].map(([s, c]) => (
                                <span key={c} className="vlx-currency">
                                    <span className="sym">{s}</span>{c}
                                </span>
                            ))}
                        </div>
                        <p className="vlx-story__end">
                            Different currencies. Different accounts.
                            <br /><span className="soft">One financial life. One VAULT.</span>
                        </p>
                    </article>

                    {/* ── Story 02 · Cards ─────────────────────────── */}
                    <article
                        className={`vlx-story ${idx === 1 ? "is-active" : ""}`}
                        aria-hidden={idx !== 1}
                        data-testid="vlx-story-cards"
                    >
                        <div className="vlx-story__eyebrow">
                            <CreditCard size={11} style={{ verticalAlign: "-2px", marginRight: 6 }} /> Cards
                        </div>
                        <h2 className="vlx-story__h">
                            One card is easy.<br />
                            <span className="accent">Ten deserve a system.</span>
                        </h2>

                        <div className="vlx-cards" aria-hidden>
                            {["Premium", "Travel", "Rewards", "Cashback", "Business"].map((t, i) => (
                                <div key={t} className={`vlx-card c-${i + 1}`}>
                                    <span className="vlx-card__ribbon" />
                                    <div className="vlx-card__label">{t}</div>
                                    <div className="vlx-card__num">•••• {1200 + i * 37}</div>
                                </div>
                            ))}
                        </div>

                        <div className="vlx-microrow">
                            <div className="vlx-micro"><span className="k">Card 01</span><span className="v">Due 26 Aug</span></div>
                            <div className="vlx-micro"><span className="k">Card 02</span><span className="v">₹2,450 cashback</span></div>
                            <div className="vlx-micro"><span className="k">Card 03</span><span className="v">Statement ready</span></div>
                            <div className="vlx-micro"><span className="k">Card 04</span><span className="v">Payment complete ✓</span></div>
                        </div>

                        <p className="vlx-story__end">
                            Know every card.
                            <span className="soft"> Without remembering every card.</span>
                        </p>
                    </article>

                    {/* ── Story 03 · Wealth ────────────────────────── */}
                    <article
                        className={`vlx-story ${idx === 2 ? "is-active" : ""}`}
                        aria-hidden={idx !== 2}
                        data-testid="vlx-story-wealth"
                    >
                        <div className="vlx-story__eyebrow">
                            <Wallet size={11} style={{ verticalAlign: "-2px", marginRight: 6 }} /> Wealth
                        </div>
                        <h2 className="vlx-story__h">
                            Wealth takes many forms.<br />
                            <span className="accent">One complete picture.</span>
                        </h2>

                        <div className="vlx-assets">
                            <div className="vlx-asset"><div className="vlx-asset__k">Equities</div><div className="vlx-asset__v">Growth</div></div>
                            <div className="vlx-asset"><div className="vlx-asset__k">Fixed Income</div><div className="vlx-asset__v">Stability</div></div>
                            <div className="vlx-asset"><div className="vlx-asset__k">Gold</div><div className="vlx-asset__v">Preservation</div></div>
                            <div className="vlx-asset"><div className="vlx-asset__k">Real Estate</div><div className="vlx-asset__v">Legacy</div></div>
                            <div className="vlx-asset"><div className="vlx-asset__k">Cash</div><div className="vlx-asset__v">Liquidity</div></div>
                            <div className="vlx-asset"><div className="vlx-asset__k">Global</div><div className="vlx-asset__v">Diversify</div></div>
                            <div className="vlx-asset"><div className="vlx-asset__k">Alternatives</div><div className="vlx-asset__v">Optional</div></div>
                            <div className="vlx-asset"><div className="vlx-asset__k">Retirement</div><div className="vlx-asset__v">Long-view</div></div>
                        </div>

                        <div className="vlx-formula" aria-hidden>
                            <span className="vlx-formula__k">Assets</span>
                            <span className="vlx-formula__op">−</span>
                            <span className="vlx-formula__k">Liabilities</span>
                            <span className="vlx-formula__op">=</span>
                            <span className="vlx-formula__nw">Net Worth</span>
                        </div>

                        <p className="vlx-story__disclaimer">
                            VAULT helps organise financial information and does not provide investment advice.
                        </p>
                    </article>

                    {/* ── Story 04 · Life ──────────────────────────── */}
                    <article
                        className={`vlx-story ${idx === 3 ? "is-active" : ""}`}
                        aria-hidden={idx !== 3}
                        data-testid="vlx-story-life"
                    >
                        <div className="vlx-story__eyebrow">
                            <Clock size={11} style={{ verticalAlign: "-2px", marginRight: 6 }} /> Your financial life
                        </div>
                        <h2 className="vlx-story__h">
                            Your money has a lot going on.<br />
                            <span className="accent">VAULT keeps track.</span>
                        </h2>

                        <ol className="vlx-timeline">
                            <li className="vlx-timeline__item">
                                <span className="vlx-timeline__time">08:14</span>
                                <span className="vlx-timeline__text">Salary received</span>
                            </li>
                            <li className="vlx-timeline__item">
                                <span className="vlx-timeline__time">09:42</span>
                                <span className="vlx-timeline__text">Subscription renewed</span>
                            </li>
                            <li className="vlx-timeline__item">
                                <span className="vlx-timeline__time">11:30</span>
                                <span className="vlx-timeline__text">Rewards earned</span>
                            </li>
                            <li className="vlx-timeline__item">
                                <span className="vlx-timeline__time">14:18</span>
                                <span className="vlx-timeline__text">Card payment due tomorrow</span>
                            </li>
                            <li className="vlx-timeline__item">
                                <span className="vlx-timeline__time">18:20</span>
                                <span className="vlx-timeline__text">Insurance renewal approaching</span>
                            </li>
                            <li className="vlx-timeline__item">
                                <span className="vlx-timeline__time">21:10</span>
                                <span className="vlx-timeline__text">Shared expense added</span>
                            </li>
                        </ol>

                        <div className="vlx-summary" aria-hidden>
                            <span className="vlx-summary__chip">3 payments approaching</span>
                            <span className="vlx-summary__chip">2 subscriptions renewing</span>
                            <span className="vlx-summary__chip">1 policy expires soon</span>
                            <span className="vlx-summary__chip">Rewards waiting</span>
                        </div>

                        <p className="vlx-story__end">
                            Nothing urgent.
                            <span className="soft"> Because nothing was forgotten.</span>
                        </p>
                    </article>

                    {/* ── Story 05 · Together ──────────────────────── */}
                    <article
                        className={`vlx-story ${idx === 4 ? "is-active" : ""}`}
                        aria-hidden={idx !== 4}
                        data-testid="vlx-story-together"
                    >
                        <div className="vlx-story__eyebrow">
                            <Users size={11} style={{ verticalAlign: "-2px", marginRight: 6 }} /> VAULT Together
                        </div>
                        <h2 className="vlx-story__h">
                            A life together doesn&rsquo;t mean everything has to be shared.
                        </h2>
                        <p className="vlx-story__sub">
                            Shared where it matters. Private where it counts.
                        </p>

                        <div className="vlx-together">
                            <div className="vlx-col c-mine">
                                <div className="vlx-col__k">Mine</div>
                                <ul className="vlx-col__list">
                                    <li>My cards</li>
                                    <li>My investments</li>
                                    <li>My documents</li>
                                </ul>
                            </div>
                            <div className="vlx-col c-partner">
                                <div className="vlx-col__k">Partner</div>
                                <ul className="vlx-col__list">
                                    <li>Partner cards</li>
                                    <li>Partner investments</li>
                                    <li>Partner documents</li>
                                </ul>
                            </div>
                            <div className="vlx-joint" aria-hidden>
                                <div className="vlx-joint__k">Joint</div>
                                <div className="vlx-joint__items">
                                    <span>Home loan</span>
                                    <span>Shared account</span>
                                    <span>Insurance</span>
                                    <span>Household</span>
                                </div>
                            </div>
                        </div>

                        <p className="vlx-story__end">
                            Household overview. Shared responsibilities. Important dates.
                            <br /><span className="soft">VAULT Together.</span>
                        </p>
                    </article>
                </div>
            </div>

            <div className="vlx-indicator" data-testid="vlx-indicator">
                <div className="vlx-indicator__dots" role="tablist" aria-label="VAULT story chapters">
                    {STORIES.map((s, i) => (
                        <button
                            key={s.key}
                            type="button"
                            role="tab"
                            aria-selected={idx === i}
                            className={`vlx-indicator__dot ${idx === i ? "is-active" : ""}`}
                            onClick={() => pick(i)}
                            data-testid={`vlx-dot-${s.key}`}
                        >
                            {s.label}
                        </button>
                    ))}
                </div>
                <div className="vlx-indicator__progress" aria-hidden>
                    <div
                        key={`${idx}-${paused ? "p" : "r"}`}
                        className={`vlx-indicator__progress-bar ${paused ? "" : "is-running"}`}
                    />
                </div>
            </div>
        </section>
    );
}
